const A11Y_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm8.4 5.2-3.4.8A4.2 4.2 0 0 0 14 7h-4c-1.2 0-2.3.5-3 1.3l-3.4-.8.8 1.8 2.8.6V22h2.3v-7.2h3v7.2H14V9.9l3.8-.8.6-1.9z"/></svg>`;

const VAGUE_LINK_TEXT = new Set([
  'click here',
  'click',
  'here',
  'read more',
  'more',
  'link',
  'this',
  'this link',
  'learn more',
  'details',
  'info',
]);

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const snippet = (value, max = 80) => {
  const text = String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) {
    return '';
  }
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

const parseCssColor = (value) => {
  if (
    !value ||
    value === 'transparent' ||
    value === 'inherit' ||
    value === 'initial'
  ) {
    return null;
  }

  const hex = value.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    let raw = hex[1];
    if (raw.length === 3) {
      raw = raw
        .split('')
        .map((char) => char + char)
        .join('');
    }
    return [
      parseInt(raw.slice(0, 2), 16),
      parseInt(raw.slice(2, 4), 16),
      parseInt(raw.slice(4, 6), 16),
    ];
  }

  const rgb = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) {
    const alphaMatch = value.match(
      /rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*([\d.]+)/i,
    );
    if (alphaMatch && Number(alphaMatch[1]) === 0) {
      return null;
    }
    return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  }

  return null;
};

const relativeLuminance = ([red, green, blue]) => {
  const toLinear = (channel) => {
    const value = channel / 255;
    return value <= 0.03928
      ? value / 12.92
      : Math.pow((value + 0.055) / 1.055, 2.4);
  };
  return (
    0.2126 * toLinear(red) + 0.7152 * toLinear(green) + 0.0722 * toLinear(blue)
  );
};

const contrastRatio = (first, second) => {
  const lighter = Math.max(relativeLuminance(first), relativeLuminance(second));
  const darker = Math.min(relativeLuminance(first), relativeLuminance(second));
  return (lighter + 0.05) / (darker + 0.05);
};

const resolveBackground = (element, view) => {
  let current = element;
  while (current && current !== view.document) {
    const color = parseCssColor(view.getComputedStyle(current).backgroundColor);
    if (color) {
      return color;
    }
    current = current.parentElement;
  }
  return [255, 255, 255];
};

const visibleText = (node) =>
  (node.innerText || node.textContent || '').replace(/\s+/g, ' ').trim();

const headingLevel = (node) => Number(node.tagName.slice(1));

const scanAccessibility = (editorInstance) => {
  const root = editorInstance.editor;
  const view = editorInstance.ew || window;
  if (!root) {
    return [];
  }

  const issues = [];
  let index = 0;
  const addIssue = (issue) => {
    issues.push({ id: `a11y-${index++}`, ...issue });
  };

  root.querySelectorAll('img').forEach((node) => {
    if (node.getAttribute('role') === 'presentation') {
      return;
    }
    const alt = node.getAttribute('alt');
    if (alt === null) {
      addIssue({
        type: 'missing-alt',
        severity: 'error',
        title: 'Image missing alternative text',
        description:
          'Screen readers cannot describe this image. Add short alt text that explains the image.',
        context: snippet(node.getAttribute('src') || 'image'),
        remedy: 'Enter alternative text for the image.',
        inputLabel: 'Alternative text',
        node,
      });
      return;
    }
    const fileName = (node.getAttribute('src') || '').split('/').pop() || '';
    if (
      alt.trim() &&
      fileName &&
      alt.trim().toLowerCase() === fileName.toLowerCase()
    ) {
      addIssue({
        type: 'filename-alt',
        severity: 'warn',
        title: 'Image alt text is a file name',
        description:
          'Alt text should describe the image, not repeat the file name.',
        context: snippet(alt),
        remedy: 'Replace the alt text with a short description.',
        inputLabel: 'Alternative text',
        defaultValue: alt,
        node,
      });
    }
  });

  root.querySelectorAll('a[href]').forEach((node) => {
    const text = visibleText(node);
    const hasImage = node.querySelector('img');
    if (!text && !hasImage) {
      addIssue({
        type: 'empty-link',
        severity: 'error',
        title: 'Link has no text',
        description:
          'Empty links are announced as “link” with no destination. Add descriptive link text.',
        context: snippet(node.getAttribute('href') || ''),
        remedy: 'Enter text that describes where the link goes.',
        inputLabel: 'Link text',
        node,
      });
      return;
    }
    if (text && VAGUE_LINK_TEXT.has(text.toLowerCase())) {
      addIssue({
        type: 'vague-link',
        severity: 'warn',
        title: 'Link text is not descriptive',
        description: `“${text}” does not tell users where the link goes. Use the destination or purpose instead.`,
        context: snippet(text),
        remedy: 'Replace the link text with a clear description.',
        inputLabel: 'Link text',
        defaultValue: text,
        node,
      });
    }
  });

  const headings = [...root.querySelectorAll('h1, h2, h3, h4, h5, h6')];
  let previousLevel = 0;
  headings.forEach((node) => {
    const level = headingLevel(node);
    if (previousLevel && level > previousLevel + 1) {
      addIssue({
        type: 'heading-skip',
        severity: 'warn',
        title: 'Heading level skipped',
        description: `This heading jumps from H${previousLevel} to H${level}. Use H${
          previousLevel + 1
        } so the outline stays in order.`,
        context: snippet(visibleText(node) || `H${level}`),
        remedy: `Change this heading to H${previousLevel + 1}.`,
        fixLabel: `Change to H${previousLevel + 1}`,
        targetLevel: previousLevel + 1,
        node,
      });
    }
    previousLevel = level;
  });

  root.querySelectorAll('table').forEach((node) => {
    if (node.querySelector('th')) {
      return;
    }
    addIssue({
      type: 'table-headers',
      severity: 'error',
      title: 'Table has no header cells',
      description:
        'Tables need header cells so screen readers can announce column names.',
      context: snippet(visibleText(node.querySelector('td')) || 'table'),
      remedy: 'Convert the first row into header cells.',
      fixLabel: 'Use first row as headers',
      node,
    });
  });

  root.querySelectorAll('iframe').forEach((node) => {
    const title = (node.getAttribute('title') || '').trim();
    if (!title) {
      addIssue({
        type: 'iframe-title',
        severity: 'error',
        title: 'Embedded frame missing title',
        description:
          'Iframes need a title so assistive technology can describe the embedded content.',
        context: snippet(node.getAttribute('src') || 'iframe'),
        remedy: 'Enter a short title for this embed.',
        inputLabel: 'Frame title',
        node,
      });
    }
  });

  root.querySelectorAll('[style]').forEach((node) => {
    if (
      !visibleText(node) ||
      node.closest('table, img, iframe, a, svg, .jodit-formula, .katex')
    ) {
      return;
    }
    const style = view.getComputedStyle(node);
    const color = parseCssColor(style.color);
    const background = resolveBackground(node, view);
    if (!color || !background) {
      return;
    }
    const ratio = contrastRatio(color, background);
    if (ratio < 4.5) {
      addIssue({
        type: 'contrast',
        severity: 'warn',
        title: 'Text colour contrast is too low',
        description: `This text contrast is ${ratio.toFixed(
          1,
        )}:1. WCAG AA requires at least 4.5:1 for normal text.`,
        context: snippet(visibleText(node)),
        remedy: 'Reset the text colour so it uses the default readable colour.',
        fixLabel: 'Reset text colour',
        node,
      });
    }
  });

  return issues;
};

const applyFix = (issue, value) => {
  const { node, type, targetLevel } = issue;
  if (!node || !node.parentNode) {
    return false;
  }

  switch (type) {
    case 'missing-alt':
    case 'filename-alt': {
      const alt = String(value || '').trim();
      if (!alt) {
        return false;
      }
      node.setAttribute('alt', alt);
      return true;
    }
    case 'empty-link':
    case 'vague-link': {
      const text = String(value || '').trim();
      if (!text) {
        return false;
      }
      node.textContent = text;
      return true;
    }
    case 'heading-skip': {
      const next = node.ownerDocument.createElement(`h${targetLevel}`);
      [...node.attributes].forEach((attr) => {
        next.setAttribute(attr.name, attr.value);
      });
      next.innerHTML = node.innerHTML;
      node.parentNode.replaceChild(next, node);
      return true;
    }
    case 'table-headers': {
      const firstRow = node.querySelector('tr');
      if (!firstRow) {
        return false;
      }
      firstRow.querySelectorAll('td').forEach((cell) => {
        const header = node.ownerDocument.createElement('th');
        header.innerHTML = cell.innerHTML;
        [...cell.attributes].forEach((attr) => {
          header.setAttribute(attr.name, attr.value);
        });
        cell.parentNode.replaceChild(header, cell);
      });
      return true;
    }
    case 'iframe-title': {
      const title = String(value || '').trim();
      if (!title) {
        return false;
      }
      node.setAttribute('title', title);
      return true;
    }
    case 'contrast': {
      node.style.removeProperty('color');
      if (!node.getAttribute('style')) {
        node.removeAttribute('style');
      }
      return true;
    }
    default:
      return false;
  }
};

const clearHighlights = (root) => {
  root
    ?.querySelectorAll('.jodit-a11y-highlight')
    .forEach((node) => node.classList.remove('jodit-a11y-highlight'));
};

const highlightIssue = (editorInstance, node) => {
  clearHighlights(editorInstance.editor);
  if (!node || !node.parentNode) {
    return;
  }
  node.classList.add('jodit-a11y-highlight');
  if (typeof node.scrollIntoView === 'function') {
    node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
};

const renderIssueList = (container, issues) => {
  if (!issues.length) {
    container.innerHTML = `<div class="jodit-a11y-dialog__success">No accessibility issues found.</div>`;
    return;
  }

  container.innerHTML = issues
    .map((issue) => {
      const needsInput = Boolean(issue.inputLabel);
      return `<article class="jodit-a11y-item jodit-a11y-item--${
        issue.severity
      }" data-issue-id="${issue.id}">
        <div class="jodit-a11y-item__header">
          <span class="jodit-a11y-item__badge">${issue.severity}</span>
          <h3 class="jodit-a11y-item__title">${escapeHtml(issue.title)}</h3>
        </div>
        <p class="jodit-a11y-item__description">${escapeHtml(
          issue.description,
        )}</p>
        ${
          issue.context
            ? `<p class="jodit-a11y-item__context">${escapeHtml(
                issue.context,
              )}</p>`
            : ''
        }
        <p class="jodit-a11y-item__remedy"><strong>Fix:</strong> ${escapeHtml(
          issue.remedy,
        )}</p>
        ${
          needsInput
            ? `<label class="jodit-a11y-item__label">${escapeHtml(
                issue.inputLabel,
              )}</label>
               <input class="jodit-input jodit-a11y-item__input" type="text" value="${escapeHtml(
                 issue.defaultValue || '',
               )}" />`
            : ''
        }
        <button type="button" class="jodit-ui-button jodit-ui-button_variant_primary jodit-a11y-item__fix" data-issue-id="${
          issue.id
        }">${escapeHtml(issue.fixLabel || 'Apply fix')}</button>
      </article>`;
    })
    .join('');
};

const openA11yDialog = (editorInstance) => {
  const dialog = editorInstance.dlg({
    closeOnClickOverlay: true,
    resizable: true,
    minWidth: 520,
  });

  const form = dialog.c.fromHTML(`<div class="jodit-a11y-dialog">
    <div class="jodit-a11y-dialog__summary"></div>
    <div class="jodit-a11y-dialog__list"></div>
  </div>`);

  const summaryEl = form.querySelector('.jodit-a11y-dialog__summary');
  const listEl = form.querySelector('.jodit-a11y-dialog__list');
  let issues = [];

  const refresh = () => {
    clearHighlights(editorInstance.editor);
    issues = scanAccessibility(editorInstance);
    const errors = issues.filter((issue) => issue.severity === 'error').length;
    const warnings = issues.filter((issue) => issue.severity === 'warn').length;
    summaryEl.textContent = issues.length
      ? `${issues.length} issue${
          issues.length === 1 ? '' : 's'
        } found (${errors} error${
          errors === 1 ? '' : 's'
        }, ${warnings} warning${warnings === 1 ? '' : 's'})`
      : 'No accessibility issues found';
    renderIssueList(listEl, issues);
  };

  dialog.e.on(listEl, 'click', (event) => {
    const item = event.target.closest('.jodit-a11y-item');
    if (!item) {
      return;
    }
    const issue = issues.find((entry) => entry.id === item.dataset.issueId);
    if (!issue) {
      return;
    }
    highlightIssue(editorInstance, issue.node);

    const fixButton = event.target.closest('.jodit-a11y-item__fix');
    if (!fixButton) {
      return;
    }

    const input = item.querySelector('.jodit-a11y-item__input');
    clearHighlights(editorInstance.editor);
    const applied = applyFix(issue, input ? input.value : '');
    if (!applied) {
      highlightIssue(editorInstance, issue.node);
      if (input) {
        input.focus();
      }
      return;
    }
    editorInstance.synchronizeValues();
    refresh();
  });

  const closeBtn = dialog.c.fromHTML(
    '<button type="button" class="jodit-ui-button">Close</button>',
  );
  const rescanBtn = dialog.c.fromHTML(
    '<button type="button" class="jodit-ui-button jodit-ui-button_variant_primary">Scan again</button>',
  );

  dialog.e.on(closeBtn, 'click', () => {
    clearHighlights(editorInstance.editor);
    dialog.close();
  });
  dialog.e.on(rescanBtn, 'click', refresh);
  dialog.e.on(dialog, 'afterClose', () => {
    clearHighlights(editorInstance.editor);
  });

  refresh();
  dialog.setFooter([rescanBtn, closeBtn]);
  dialog.open(form, 'Accessibility checker', true, true);
};

export { openA11yDialog };

export const getA11yButton = () => ({
  name: 'a11yCheck',
  tooltip: 'Accessibility checker',
  icon: A11Y_ICON,
  exec: (editorInstance) => {
    openA11yDialog(editorInstance);
  },
});
